// #1031: the browser read for My Trades' Polymarket US history (GET /api/polymarket-us/my-trades).
//
// The caller's own fills and positions on view_own accounts, from the relay's shared reconciled readers.
// Like fillsClient, the decoder is whole-reply strict (one malformed row rejects everything, so the page
// shows US as unavailable rather than a silently shortened history), and every quantity, price and USD
// amount stays an exact decimal string. A null P&L is unknown, never zero.

import { sendUsBrowserRequest } from './browserTradingClient';
import { UsClientError, type UsClientSession } from './tradingClient';

export interface UsMyTradeFill {
  accountId: string; executionId: string; intentId: string;
  marketSlug: string; marketTitle: string | null;
  outcome: 'yes' | 'no'; action: 'buy' | 'sell';
  quantity: string; price: string | null;
  priceBasis: 'yes' | 'provisional' | 'unverified' | 'missing';
  executedAt: string | null; observedAtMs: number;
  feeUsd: string | null;
}
export type UsPositionEvidence = 'complete' | 'fee_incomplete' | 'orphan_sell' | 'cost_basis_unknown' | 'payout_unverified';
export interface UsMyTradePosition {
  accountId: string; marketSlug: string; marketTitle: string | null; outcome: 'yes' | 'no';
  /** `closed`: fully sold before any settlement. */
  status: 'open' | 'settled' | 'closed';
  /** '' when the holding cannot be established. */
  netQuantity: string;
  costBasisUsd: string | null; tradingRealizedUsd: string | null; realizedFeesUsd: string | null;
  settlementPnlUsd: string | null; totalRealizedNetUsd: string | null;
  settlementId: string | null; settledAt: string | null; lastFillAt: string | null;
  evidence: UsPositionEvidence;
  /** #1227 — the fold's lifecycle totals (settledPositions.ts), for the K/P-shaped rows, exact
   *  decimals: quantities null unless the recorded fills are whole and explain the holding; amounts
   *  null wherever the cost basis is, a side's fees also when any fee on it is unknown. */
  /** Missing on an older relay: unknown, never inferred from the newest fill window. */
  hasRecordedSale?: boolean;
  boughtQuantity: string | null; soldQuantity: string | null; sellProceedsUsd: string | null;
  buyFeesUsd: string | null; sellFeesUsd: string | null;
}
export interface UsMyTrades {
  complete: boolean; reasons: string[];
  fills: UsMyTradeFill[]; positions: UsMyTradePosition[]; withheld: number;
}
export type UsMyTradesResult =
  | ({ ok: true } & UsMyTrades)
  | { ok: false; code: 'US_MY_TRADES_MALFORMED' | 'US_MY_TRADES_UNAVAILABLE' | 'US_CLIENT_CONTEXT_CHANGED' | 'US_CLIENT_AUTH_REQUIRED' };

const MAX_FILLS = 200;
const ID = /^[A-Za-z0-9_-]{1,256}$/;
const DECIMAL = /^(?:0|[1-9]\d*)(?:\.\d{1,12})?$/;
const SIGNED = /^-?(?:0|[1-9]\d*)(?:\.\d{1,12})?$/;
const TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/;
const REASON = /^[a-z_]{1,64}$/;
const FILL_KEYS = ['accountId', 'executionId', 'intentId', 'marketSlug', 'marketTitle', 'outcome', 'action', 'quantity',
  'price', 'priceBasis', 'executedAt', 'observedAtMs', 'feeUsd'].sort().join();
const POSITION_BASE_KEYS = ['accountId', 'marketSlug', 'marketTitle', 'outcome', 'status', 'netQuantity', 'costBasisUsd',
  'tradingRealizedUsd', 'realizedFeesUsd', 'settlementPnlUsd', 'totalRealizedNetUsd', 'settlementId', 'settledAt',
  'lastFillAt', 'evidence'];
const POSITION_TOTAL_KEYS = ['boughtQuantity', 'soldQuantity', 'sellProceedsUsd', 'buyFeesUsd', 'sellFeesUsd'];
const POSITION_KEYS = new Set([...POSITION_BASE_KEYS, ...POSITION_TOTAL_KEYS, 'hasRecordedSale']);
const EVIDENCE: readonly UsPositionEvidence[] = ['complete', 'fee_incomplete', 'orphan_sell', 'cost_basis_unknown', 'payout_unverified'];

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isId = (v: unknown): v is string => typeof v === 'string' && ID.test(v);
const isTitle = (v: unknown): v is string | null => v === null || (typeof v === 'string' && v.length <= 1000);
const isTime = (v: unknown): v is string | null => v === null || (typeof v === 'string' && TIME.test(v));
const isSigned = (v: unknown): v is string | null => v === null || (typeof v === 'string' && SIGNED.test(v));
const isOutcome = (v: unknown): v is 'yes' | 'no' => v === 'yes' || v === 'no';
const isQuantity = (v: unknown): v is string | null => v === null || (typeof v === 'string' && DECIMAL.test(v));

function decodeFill(v: unknown): UsMyTradeFill | null {
  if (!isRecord(v) || Object.keys(v).sort().join() !== FILL_KEYS) return null;
  const { accountId, executionId, intentId, marketSlug, marketTitle, outcome, action, quantity, price, priceBasis,
    executedAt, observedAtMs, feeUsd } = v;
  if (!isId(accountId) || !isId(executionId) || !isId(intentId) || !isId(marketSlug) || !isTitle(marketTitle)) return null;
  if (!isOutcome(outcome) || (action !== 'buy' && action !== 'sell')) return null;
  if (typeof quantity !== 'string' || !DECIMAL.test(quantity)) return null;
  if (priceBasis !== 'yes' && priceBasis !== 'provisional' && priceBasis !== 'unverified' && priceBasis !== 'missing') return null;
  // A price is present exactly for a YES-basis YES fill or a provisional (1 − yes) NO fill.
  const priced = priceBasis === 'yes' || priceBasis === 'provisional';
  if (priced ? !(typeof price === 'string' && DECIMAL.test(price)) : price !== null) return null;
  if ((priceBasis === 'yes' && outcome !== 'yes') || (priceBasis === 'provisional' && outcome !== 'no')) return null;
  if (!isTime(executedAt) || typeof observedAtMs !== 'number' || !Number.isSafeInteger(observedAtMs) || observedAtMs < 0) return null;
  if (!isSigned(feeUsd)) return null;
  return { accountId, executionId, intentId, marketSlug, marketTitle, outcome, action, quantity, price: price as string | null,
    priceBasis, executedAt, observedAtMs, feeUsd };
}

function decodePosition(v: unknown): UsMyTradePosition | null {
  if (!isRecord(v) || Object.keys(v).some(k => !POSITION_KEYS.has(k))
    || POSITION_BASE_KEYS.some(k => !Object.prototype.hasOwnProperty.call(v, k))) return null;
  // A new bundle can land before its relay. Accept the complete old shape, but never a
  // partly transmitted lifecycle extension or a malformed known value.
  const suppliedTotals = POSITION_TOTAL_KEYS.filter(k => Object.prototype.hasOwnProperty.call(v, k)).length;
  if (suppliedTotals !== 0 && suppliedTotals !== POSITION_TOTAL_KEYS.length) return null;
  if (Object.prototype.hasOwnProperty.call(v, 'hasRecordedSale') && typeof v.hasRecordedSale !== 'boolean') return null;
  const { accountId, marketSlug, marketTitle, outcome, status, netQuantity, costBasisUsd, tradingRealizedUsd,
    realizedFeesUsd, settlementPnlUsd, totalRealizedNetUsd, settlementId, settledAt, lastFillAt, evidence,
    boughtQuantity = null, soldQuantity = null, sellProceedsUsd = null, buyFeesUsd = null, sellFeesUsd = null } = v;
  if (!isId(accountId) || !isId(marketSlug) || !isTitle(marketTitle) || !isOutcome(outcome)) return null;
  if ((status !== 'open' && status !== 'settled' && status !== 'closed') || typeof netQuantity !== 'string'
    || (netQuantity !== '' && !SIGNED.test(netQuantity))) return null;
  if (![costBasisUsd, tradingRealizedUsd, realizedFeesUsd, settlementPnlUsd, totalRealizedNetUsd, buyFeesUsd, sellFeesUsd]
    .every(isSigned)) return null;
  if (!isQuantity(boughtQuantity) || !isQuantity(soldQuantity) || !isQuantity(sellProceedsUsd)) return null;
  if (!(settlementId === null || (typeof settlementId === 'string' && settlementId.length <= 256))) return null;
  if (!isTime(settledAt) || !isTime(lastFillAt)) return null;
  const known = EVIDENCE.find(e => e === evidence);
  if (!known) return null;
  return { accountId, marketSlug, marketTitle, outcome, status, netQuantity, costBasisUsd: costBasisUsd as string | null,
    tradingRealizedUsd: tradingRealizedUsd as string | null, realizedFeesUsd: realizedFeesUsd as string | null,
    settlementPnlUsd: settlementPnlUsd as string | null, totalRealizedNetUsd: totalRealizedNetUsd as string | null,
    settlementId, settledAt, lastFillAt, evidence: known, boughtQuantity, soldQuantity, sellProceedsUsd,
    ...(typeof v.hasRecordedSale === 'boolean' ? { hasRecordedSale: v.hasRecordedSale } : {}),
    buyFeesUsd: buyFeesUsd as string | null, sellFeesUsd: sellFeesUsd as string | null };
}

export function decodeUsMyTrades(body: unknown): UsMyTrades | null {
  if (!isRecord(body) || body.venue !== 'polymarket_us' || body.source !== 'saved_evidence'
    || typeof body.complete !== 'boolean' || !Array.isArray(body.reasons)
    || !body.reasons.every(r => typeof r === 'string' && REASON.test(r))
    || typeof body.withheld !== 'number' || !Number.isSafeInteger(body.withheld) || body.withheld < 0
    || !Array.isArray(body.fills) || body.fills.length > MAX_FILLS || !Array.isArray(body.positions)) return null;
  const fills: UsMyTradeFill[] = [];
  for (const raw of body.fills) {
    const f = decodeFill(raw);
    if (!f) return null;
    fills.push(f);
  }
  const positions: UsMyTradePosition[] = [];
  for (const raw of body.positions) {
    const p = decodePosition(raw);
    if (!p) return null;
    positions.push(p);
  }
  return { complete: body.complete, reasons: body.reasons as string[], fills, positions, withheld: body.withheld };
}

export async function readPolymarketUsMyTrades(session: UsClientSession,
  send: typeof sendUsBrowserRequest = sendUsBrowserRequest): Promise<UsMyTradesResult> {
  let response: Response;
  try {
    response = await send('/api/polymarket-us/my-trades', { method: 'GET', cache: 'no-store', headers: { 'X-US-Reporting-Version': '2' } }, session);
  } catch (error) {
    if (error instanceof UsClientError
      && (error.code === 'US_CLIENT_CONTEXT_CHANGED' || error.code === 'US_CLIENT_AUTH_REQUIRED')) {
      return { ok: false, code: error.code };
    }
    return { ok: false, code: 'US_MY_TRADES_UNAVAILABLE' };
  }
  if (!response.ok) return { ok: false, code: 'US_MY_TRADES_UNAVAILABLE' };
  let json: unknown;
  try { json = await response.json(); } catch { return { ok: false, code: 'US_MY_TRADES_MALFORMED' }; }
  const decoded = decodeUsMyTrades(json);
  return decoded ? { ok: true, ...decoded } : { ok: false, code: 'US_MY_TRADES_MALFORMED' };
}
